/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type AdjustProformaInput,
  type BulkConvertProformasInput,
  type ConvertProformaInput,
  type GenerateProformaCycleInput,
  type ProformaStatus,
} from '@therapy/shared-validators';

import { apiClient } from './client.js';

export interface Proforma {
  readonly id: string;
  readonly clientId: string;
  readonly clientName: string;
  readonly proformaNumber: string;
  readonly status: ProformaStatus;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly sessionsCount: number;
  readonly sessionFeePaise: number;
  readonly subtotalPaise: number;
  readonly discountPaise: number;
  readonly gstPaise: number;
  readonly totalPaise: number;
  readonly note: string | null;
  readonly declineReason: string | null;
  readonly invoiceId: string | null;
  readonly invoiceNumber: string | null;
  readonly sentAt: string | null;
  readonly respondedAt: string | null;
  readonly convertedAt: string | null;
  readonly createdAt: string;
}

export interface ProformaCycleResult {
  readonly month: string;
  readonly generated: number;
  readonly skipped: number;
}

export interface ProformaConvertResult {
  readonly converted: number;
  readonly skipped: number;
}

const BASE = '/proformas';

export const proformasApi = {
  async mine(): Promise<readonly Proforma[]> {
    const { data } = await apiClient.get<readonly Proforma[]>(`${BASE}/mine`);
    return data;
  },
  async list(status?: ProformaStatus): Promise<readonly Proforma[]> {
    const { data } = await apiClient.get<readonly Proforma[]>(BASE, {
      params: status !== undefined ? { status } : {},
    });
    return data;
  },
  async generate(input: GenerateProformaCycleInput): Promise<ProformaCycleResult> {
    const { data } = await apiClient.post<ProformaCycleResult>(`${BASE}/generate`, input);
    return data;
  },
  async adjust(id: string, input: AdjustProformaInput): Promise<Proforma> {
    const { data } = await apiClient.patch<Proforma>(`${BASE}/${id}/adjust`, input);
    return data;
  },
  async send(id: string): Promise<Proforma> {
    const { data } = await apiClient.post<Proforma>(`${BASE}/${id}/send`);
    return data;
  },
  async approve(id: string): Promise<Proforma> {
    const { data } = await apiClient.post<Proforma>(`${BASE}/${id}/approve`);
    return data;
  },
  async decline(id: string, reason: string): Promise<Proforma> {
    const { data } = await apiClient.post<Proforma>(`${BASE}/${id}/decline`, { reason });
    return data;
  },
  async convert(id: string, input: ConvertProformaInput): Promise<Proforma> {
    const { data } = await apiClient.post<Proforma>(`${BASE}/${id}/convert`, input);
    return data;
  },
  async convertBulk(input: BulkConvertProformasInput): Promise<ProformaConvertResult> {
    const { data } = await apiClient.post<ProformaConvertResult>(`${BASE}/convert`, input);
    return data;
  },
};
